import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import HttpInvoiceRepository from "../../../../../api/request/Invoice/Invoice.service";
import { loadStripe, StripeConstructorOptions } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { I18nContext } from "../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../context/GetLifeContext.context";
import { DataI } from "../../../../../api/request/Lead/Interfaces/DataInterface.response";
import { PricingI } from "../../../../../api/request/Lead/Interfaces/PricingInterface";
import { Promotion } from "../../../../../api/request/Lead/Interfaces/ProductInterface.response";
import HttpPaymentRepository from "../../../../../api/request/Payment/Payment.service";
import {
  AvailablePaymentMethodI,
  DataCardI,
} from "../../../../../api/request/Payment/Interfaces/PaymentMethodI.response";
import { getNextFee } from "../../../../../utils/feeCollection";
import { isNativeMode } from "../../../../../native/nativeMode";
import { dashboardPrice } from "../components/pricing/dashboardPrice";
import ConditionsModalAB from "../../../../../components/ConditionsModal/ConditionsModalAB";
import ModalPolicy from "../Policy/ModalPolicy/ModalPolicy.component";
import {
  UpcomingChargeCard,
  PaymentMethodCard,
  InvoiceHistory,
  Invoice,
} from "../components/billingPage";
import { tracker } from "../../../../../helpers/Tracking";

interface BillingPageProps {
  userData?: DataI;
  pricing?: PricingI;
}


const BillingPage = ({ userData, pricing }: BillingPageProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token } = useContext(GetlifeContext);

  const [dataCard, setDataCard] = useState<DataCardI | null>(null);
  const [setupIntent, setSetupIntent] = useState<{ clientSecret?: string } | null>(null);
  const [availablePaymentMethod, setAvailablePaymentMethod] =
    useState<AvailablePaymentMethodI | null>(null);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [methodLinkFailed, setMethodLinkFailed] = useState(false);
  // Portal Cliente native: el método se cambia en la página de Stripe del
  // setup-link (ms-payment), no con el SetupIntent del funnel de alta.
  const native = isNativeMode();

  const invoiceRepository = useMemo(
    () => new HttpInvoiceRepository(leadId, token),
    [leadId, token]
  );

  const paymentRepository = useMemo(
    () => new HttpPaymentRepository(leadId, token),
    [leadId, token]
  );

  const fetchDataCard = useCallback(async () => {
    try {
      const res = await paymentRepository.getDataCard();
      setDataCard(res);
    } catch {
      setDataCard(null);
    }
  }, [paymentRepository]);

  useEffect(() => {
    if (!leadId || !token) return;
    fetchDataCard();
    if (!native) {
      paymentRepository
        .setup()
        .then((res) => setSetupIntent(res))
        .catch(() => setSetupIntent(null));
      paymentRepository
        .availablePaymentMethod()
        .then((res) => setAvailablePaymentMethod(res))
        .catch(() => setAvailablePaymentMethod(null));
    }
    invoiceRepository
      .getBillingInvoices(leadId)
      .then((res) =>
        setInvoices(
          res.map((inv) => ({
            id: inv.id,
            date: inv.date ?? "",
            concept: translate("newDashboard.billing.monthlyPremium"),
            amount: inv.amount ?? 0,
            status: inv.status,
            payable: inv.payable,
            downloadUrl: inv.downloadUrl,
          }))
        )
      )
      .catch(() => setInvoices([]));
  }, [leadId, token, native, paymentRepository, invoiceRepository, fetchDataCard, translate]);

  const stripePromise = useMemo(
    () =>
      loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || "", {
        locale:
          (process.env
            .REACT_APP_DOMAIN_LOCALE as StripeConstructorOptions["locale"]) || "es",
        stripeAccount: process.env.REACT_APP_STRIPE_ACCOUNT,
      }),
    []
  );

  const nextFee = getNextFee(userData?.feeCollection);
  const nextPaymentDate = nextFee?.expectedPaymentDate;
  const frequency =
    nextFee?.frequency ??
    (userData?.lead?.purchasePeriod
      ? userData.lead.purchasePeriod.toUpperCase()
      : "MONTHLY");
  // Promo pricing comes from the pricing endpoint: feeCollection.discountAmount
  // is unreliable (often 0 even with an active promotion). Show the promoted
  // price and strike through the regular price when they differ.
  // Sin prima (price null, cohorte SCOR FR) los precios son null: texto neutro.
  const { regularPrice, displayPrice, hasPromo } = dashboardPrice(pricing);

  const couponInfo = pricing?.promotion
    ? {
        conditions: pricing.promotion.conditions,
        exceptions: pricing.promotion.exceptions,
        code: pricing.promotion.promotionalCode ?? undefined,
      }
    : undefined;

  const nodeConditionsModal: any = useRef(null);
  const nodeEditPaymentModal: any = useRef(null);

  const handleConditions = () => nodeConditionsModal.current?.handleShowPortal();
  const handleEditPayment = () => {
    if (!native) {
      nodeEditPaymentModal.current?.handleShowPortal();
      return;
    }
    setMethodLinkFailed(false);
    paymentRepository
      .paymentMethodSetupLink()
      .then((url) => {
        if (!url) return setMethodLinkFailed(true);
        tracker.redirecting("external");
        window.location.assign(url);
      })
      .catch(() => setMethodLinkFailed(true));
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 900 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}>
            {translate("newDashboard.billing.title")}
          </h1>
          <p style={{ fontSize: 15, color: "#6b7280", marginTop: 4 }}>
            {translate("newDashboard.billing.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <UpcomingChargeCard
            frequency={frequency}
            hasPromo={hasPromo}
            regularPrice={regularPrice}
            displayPrice={displayPrice}
            nextPaymentDate={nextPaymentDate}
            pricing={pricing}
            onShowConditions={handleConditions}
          />
          <PaymentMethodCard
            dataCard={dataCard}
            onChangeMethod={handleEditPayment}
          />
        </div>
        {methodLinkFailed && (
          <p role="alert" style={{ fontSize: 14, color: "#b91c1c", margin: 0 }}>
            Non è stato possibile aprire la pagina per cambiare il metodo di pagamento. Riprova tra qualche minuto.
          </p>
        )}

        <InvoiceHistory
          invoices={invoices}
          onDownload={(inv) =>
            invoiceRepository
              .getReceiptUrl(leadId, inv.id)
              .then((url) => url && window.open(url, "_blank"))
              .catch(() => undefined)
          }
          onPay={(inv) =>
            invoiceRepository
              .getPaymentLink(leadId, inv.id)
              .then((url) => url && window.open(url, "_blank"))
              .catch(() => undefined)
          }
        />
      </div>

      {pricing?.promotion && userData && (
        <ConditionsModalAB
          couponInfo={couponInfo}
          nodeFakeDoorModal={nodeConditionsModal}
          promotion={pricing.promotion as unknown as Promotion}
          collection={userData.feeCollection}
          period={pricing.period ?? "monthly"}
          handleSubmit={() => nodeConditionsModal.current?.handleShowPortal()}
        />
      )}

      {userData && !native && (
        <Elements stripe={stripePromise}>
          <ModalPolicy
            availablePaymentMethod={availablePaymentMethod}
            clientSecret={setupIntent?.clientSecret}
            fetchGetDataCard={fetchDataCard}
            nodeElementModal={nodeEditPaymentModal}
            userData={userData.lead}
          />
        </Elements>
      )}
    </>
  );
};

export default BillingPage;
